import { useEffect, useRef, ReactNode } from 'react';
import clsx from 'clsx';
import { CSSTransition } from 'react-transition-group';

import { Portal } from 'shared/ui/portal';
import {
  useModalLoopedTabulation,
  disableScroll,
  enableScroll,
} from 'shared/lib/utils';
import { MODAL_ANIMATION_DURATION } from 'shared/config/constants';
import { getAmpConfigFlags } from 'shared/config/amp-config';

import styles from './styles.module.scss';

export type AccessibilityAttributes = {
  'aria-labelledby': string;
  'aria-describedby'?: string;
};

export type ModalProps = {
  children: ReactNode;
  accessibilityAttributes: AccessibilityAttributes;
  isVisible: boolean;
  enableScrollOnUnmount?: boolean;
  isAssignmentPanelOpen?: boolean;
  modalClassnames?: string;
  loopedTabulationSelectors?: string;
  excludeIntercomButton?: boolean;
};

export const Modal = ({
  children,
  accessibilityAttributes,
  isVisible,
  modalClassnames,
  enableScrollOnUnmount = true,
  isAssignmentPanelOpen = false,
  loopedTabulationSelectors = '',
  excludeIntercomButton = false,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const { isNewNavigationEnabled } = getAmpConfigFlags();

  useModalLoopedTabulation({
    containerRef: modalRef,
    selectors: loopedTabulationSelectors,
    isAssignmentPanelOpen,
    excludeIntercomButton,
  });

  useEffect(() => {
    disableScroll();

    return () => {
      if (enableScrollOnUnmount) {
        enableScroll();
      }
    };
  }, [enableScrollOnUnmount]);

  // focus on the modal to allow screen readers to read modal description on its opening
  useEffect(() => {
    if (!isVisible) return;

    modalRef.current?.focus();
  }, [isVisible]);

  return (
    <Portal>
      <CSSTransition
        nodeRef={modalRef}
        in={isVisible}
        timeout={MODAL_ANIMATION_DURATION}
        classNames="modal-fade"
        unmountOnExit
      >
        <div
          className={clsx(styles.modal, modalClassnames, {
            [styles.modalWithNewNav]: isNewNavigationEnabled,
          })}
          ref={modalRef}
          tabIndex={-1}
          aria-modal="true"
          role="dialog"
          {...accessibilityAttributes}
        >
          {children}
        </div>
      </CSSTransition>
    </Portal>
  );
};
